import { Injectable } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { ToasterService } from '../../../services/toaster.service';
import { TaglocationService } from '../../../station-track-visualizer/services/taglocation.service';
import { roundedLocation } from '../../../station-track-visualizer/utils/common.util';

/** A survey row together with the track it was listed under. */
interface SurveyRowMatch {
  trackKey: string;
  item: any;
}

interface SurveyIndex {
  byUuid: Map<string, SurveyRowMatch[]>;
  byAssetId: Map<string, SurveyRowMatch[]>;
  /** Every identifier the survey mentions, in each form the matchers compare against. */
  identifiers: Set<string>;
  identifiersByTrack: Map<string, Set<string>>;
}

/** An S border tag together with the signal assets its location resolves to. */
interface BorderTagSignalBinding {
  tag: any;
  assets: any[];
}

/** The one border tag type whose location is derived from a signal, not typed in. */
const SIGNAL_BORDER_TAG_TYPE = 'S';

@Injectable({
  providedIn: 'root'
})
export class SurveyDataProcessorService {

  constructor(
    private toast: ToasterService,
    private tagLocationService: TaglocationService
  ) { }

  /* A gate only ever inherits offset_location from a previously saved gate, so a newly
     added one reaches update_rfid_placement without one and cannot be placed. Derive it
     from the gate's actual location the way tags do, leaving gates that already carry
     one untouched. `referenceStationData` supplies the tag/track reference points and
     `utilsLists` are the utils arrays holding the same gates (station data, merged
     tag-tin data, draft layout). */
  applyOffsetLocationToNewGates(referenceStationData: any, utilsLists: any[]): void {
    if (!referenceStationData?.['Tags']) return;

    const isRightToLeft = String(referenceStationData['Train Direction'] ?? '').startsWith('Nominal');
    const previousStationData = this.tagLocationService.getStationData();
    this.tagLocationService.setStationData(referenceStationData);
    const derivedOffsets = new Map<string, number>();

    utilsLists.forEach((utils: any) => {
      if (!Array.isArray(utils)) return;
      utils.forEach((asset: any) => {
        if (asset?.type_of_asset !== 'Gate' && asset?.type_of_asset !== 'sliding-boom') return;
        if (this.hasNumericValue(asset?.offset_location)) return;
        // Sliding booms hang off their gate and hold no location of their own, so they never qualify.
        if (!asset?.track_name || !this.hasNumericValue(asset?.location)) return;

        const trackLocationKey = `${asset.track_name}_${asset.location}`;
        if (!derivedOffsets.has(trackLocationKey)) {
          const offsetLocation = this.tagLocationService.getTagOffsetLocation(
            asset.track_name, Number(asset.location), isRightToLeft
          );
          if (!this.hasNumericValue(offsetLocation)) return;
          derivedOffsets.set(trackLocationKey, Number(offsetLocation));
        }
        asset.offset_location = derivedOffsets.get(trackLocationKey);
      });
    });

    // The service is shared with the visualizer, so leave it pointing where it was.
    if (previousStationData) this.tagLocationService.setStationData(previousStationData);
  }

  /* Number(null) is 0, so a null offset_location would otherwise read as a real value. */
  private hasNumericValue(value: any): boolean {
    return value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value));
  }


  /* Ensures that all necessary IDs are present in the survey data for consistent referencing, especially for dynamic UI components. This includes generating UUIDs for signals and flat section items if they are missing. */
  ensureIds(data: any): any {
    this.processSignals(data);
    this.processFlatSections(data);
    return data;
  }

  /* Processes the Signal section of the survey data, ensuring each signal and its associated assets have unique IDs for reliable referencing in the UI. */
  processSignals(data: any): void {
    if (!Array.isArray(data?.Signal)) return;

    data.Signal.forEach((signal: any) => {
      // Ensure signal id
      if (!signal.id) signal.id = crypto.randomUUID();

      // Ensure assets inside signal
      if (Array.isArray(signal?.assets)) {
        signal.assets.forEach((asset: any) => {
          if (!asset || typeof asset !== 'object') return;

          if (!asset.id) asset.id = crypto.randomUUID();
        });
      }
    });

    data.utils?.forEach((util: any) => {
      if (util.asset_id) return;
      util.asset_id = crypto.randomUUID();
    })
    data.utils = data.utils?.filter((util: any) => ['Gate', 'sliding-boom', 'roadBridge', 'center'].includes(util.type_of_asset))
  }

  /* Processes flat sections of the survey data (like Trap Point, CROSSING, etc.) to ensure each item has a unique ID. This is crucial for consistent referencing and manipulation of these items in the UI. */
  processFlatSections(data: any): void {
    const sections: string[] = [
      'utils',
      'Trap Point',
      'CROSSING',
      'DEAD END',
      'Kavach Territory Ending Point'
    ];

    sections.forEach((section: string) => {
      if (!Array.isArray(data?.[section])) return;

      data[section].forEach((item: any) => {
        if (!item || typeof item !== 'object') return;

        if (!item.id) item.id = crypto.randomUUID();
      });
    });
  }

  /* Transforms the station data by swapping the 'location' and 'display_location' properties of each trap point. This is necessary to maintain consistency in how trap point locations are represented across different parts of the application, especially when displaying them in the UI. */
  swapTrapPointsDisplayTOAbs(stationData: any) {
    return {
      ...stationData,
      "Trap Point": stationData?.["Trap Point"]?.map((trapPoint: any) => ({
        ...trapPoint,
        location: trapPoint.display_location,
        display_location: trapPoint.location
      })) || []
    };
  }

  /* Transforms the station data by swapping the 'location' and 'display_location' properties of each dead end point. This ensures that the representation of dead end points is consistent across the application, particularly in the UI where these properties may be used differently. */
  swapDeadEndPointsDisplayTOAbs(stationData: any) {
    return {
      ...stationData,
      "DEAD END": stationData?.["DEAD END"]?.map((deadEnd: any) => ({
        ...deadEnd,
        location: deadEnd.display_location,
        display_location: deadEnd.location
      })) || []
    };
  }

  /**
 * Capture row IDs from FormArray.
 */
  captureRowIds(formArray: FormArray, key: string = '__rowId'): Set<string> {
    return new Set(
      formArray.controls
        .map(ctrl => (ctrl as FormGroup).get(key)?.value)
        .filter(Boolean)
    );
  }

  /**
 * Detect structural row changes (add/remove rows).
 */
  hasRowStructureChanged(
    formArray: FormArray,
    originalIds: Set<string>
  ): boolean {

    const currentIds = this.captureRowIds(formArray);

    if (currentIds.size !== originalIds.size) return true;

    for (const id of originalIds)
      if (!currentIds.has(id)) return true;

    for (const id of currentIds)
      if (!originalIds.has(id)) return true;

    return false;
  }

  /**
   * Normalizes point IDs by ensuring they are uppercase and prefixed with 'P-'
   * @param id 
   * @returns 
   */
  normalizePointId(id: string): string {
    if (!id) return '';
    const trimmed = String(id).trim();
    if (trimmed === '') return '';
    const upperId = trimmed.toUpperCase();
    // Strip prefixes: P, P-, PT, PT-, PT/, PT//, PT/G, PT/G/
    const cleaned = upperId.replace(/^(P-?|PT-?|PT\/G\/?|PT\/+)/, '');
    if (cleaned === '') return 'P-';
    return `P-${cleaned}`;
  }  


  /**
   * Normalize mixed array of strings and objects into consistent object format
   * Handles null/undefined values gracefully
   */
  private normalizeToStations(arr: any[]): any[] {
    if (!Array.isArray(arr) || arr.length === 0) return [];

    return arr.reduce((acc, item) => {
      if (!item) return acc;
      // Convert string UUIDs to object format for consistency
      const normalized = typeof item === 'string' ? { station_uuid: item } : item;
      return [...acc, normalized];
    }, []);
  }

  /**
   * Check if any station in a group matches the target UUID
   * Returns true if match found to avoid unnecessary iterations
   */
  private hasMatchingStation(stations: any[], targetUuid: string): boolean {
    return stations.some(station => station?.station_uuid === targetUuid);
  }

  /**
   * Extract station metadata for deduplication
   * Used to create unique map entries
   */
  private extractStationMetadata(station: any): any {
    return {
      station_uuid: station.station_uuid,
      station_id: station.station_id,
      station_code: station.station_code
    };
  }

  /**
   * Get all adjacent/block station groups for a given station
   * Normalizes and groups related stations together
   */
  private getAllStationGroups(station: any): any[][] {
    const adj = station.adjacent_station_details ?? {};
    const blk = station.block_section_details ?? {};

    return [
      this.normalizeToStations(adj.left_stations),
      this.normalizeToStations(adj.right_stations),
      this.normalizeToStations(blk.left_blocks),
      this.normalizeToStations(blk.right_blocks)
    ];
  }

  /**
   * Filter stations that have connection to main_station_uuid
   * Single-pass approach: filter + map + deduplicate in one iteration
   */
  getStationList(main_station_uuid: string|undefined, stations: any[]): any[] {
    if (!Array.isArray(stations) || stations.length === 0 || !main_station_uuid) return [];

    const uniqueMap = new Map<string, any>();

    // Single pass: check connection and deduplicate simultaneously
    for (const station of stations) {
      if (!station || uniqueMap.has(station.station_uuid)) continue;

      const allGroups = this.getAllStationGroups(station);
      // Check if main station exists in any group
      if (allGroups.some(group => this.hasMatchingStation(group, main_station_uuid))) {
        uniqueMap.set(station.station_uuid, this.extractStationMetadata(station));
      }
    }

    return Array.from(uniqueMap.values());
  }

  /**
   * Current station data as a JSON file for multipart uploads, matching what the
   * RFID placement call sends so the backend parses both the same way.
   * Must be a File, not a Blob — the form-data builder recurses into anything
   * that is not a File and would flatten a Blob into junk fields.
   */
  buildStationDataFile(stationData: any, fileName: string = 'merged_tag_tin_data.json'): File | null {
    if (!stationData) return null;

    const blob = new Blob([JSON.stringify(stationData)], { type: 'application/json' });
    return new File([blob], fileName, { type: 'application/json' });
  }

  // ==================== Survey Re-Upload Location Updates ====================

  /**
   * Apply all asset location updates from a process_survey_file_second_pass
   */
  applySurveyLocationsToStationData(stationData: any, apiResponse: any, surveyOverwrite: boolean): any {
    if (!stationData || !apiResponse?.survey_data) return stationData;
    // Each re-upload starts fresh; values cleared by a previous one are stale.
    this.clearedLocations.clear();
    this.signalBorderTagLocationChanged = false;
    this.missingSignalBorderTags.clear();
    // Captured first: an S border tag is bound to its signal by location alone,
    // so the link is gone the moment the signal update rewrites that location.
    const borderTagBindings = this.captureBorderTagSignalBindings(stationData);
    const assumedBSLBOffsets = this.captureAssumedBSLBOffsets(stationData);
    this.updateSignalLocationsFromSurvey(stationData, apiResponse, surveyOverwrite);
    this.applyAssumedBSLBLocations(assumedBSLBOffsets);
    this.updatePointLocationsFromSurvey(stationData, apiResponse, surveyOverwrite);
    this.updateTrapPointLocationsFromSurvey(stationData, apiResponse, surveyOverwrite);
    this.updateDeadEndLocationsFromSurvey(stationData, apiResponse, surveyOverwrite);
    this.updateUtilLocationsFromSurvey(stationData, apiResponse, surveyOverwrite);
    this.applyBorderTagSignalBindings(borderTagBindings);
    return stationData;
  }

  hasSignalBorderTagLocationChanged(): boolean {
    return this.signalBorderTagLocationChanged;
  }

  hasSignalBorderTagMissing(stationData: any): boolean {
    return this.missingSignalBorderTags.size > 0 || this.hasCurrentlyEmptySignalBorderTags(stationData);
  }

  /** Safety net: any S manual border tag currently sitting at no location, in case it reached that state outside the capture/apply flow above. */
  private hasCurrentlyEmptySignalBorderTags(stationData: any): boolean {
    const tracks = stationData?.input_border_tags_data;
    if (!Array.isArray(tracks)) return false;

    return tracks.some((track: any) =>
      Array.isArray(track?.manual_border_tags) &&
      track.manual_border_tags.some((tag: any) =>
        tag?.type_of_tag === SIGNAL_BORDER_TAG_TYPE &&
        (tag.location === null || tag.location === undefined || tag.location === '')
      )
    );
  }

  hasNormalOrCrossoverBorderTags(stationData: any): boolean {
    const tracks = stationData?.input_border_tags_data;
    if (!Array.isArray(tracks)) return false;

    return tracks.some((track: any) =>
      Array.isArray(track?.manual_border_tags) &&
      track.manual_border_tags.some((tag: any) =>
        tag?.type_of_tag === 'N' || tag?.type_of_tag === 'X'
      )
    );
  }

  /**
   * Survey rows indexed for lookup, built once per survey_data object.
   * A uuid maps to a list because a crossing carries one uuid across two tracks,
   * giving it one row per track; the uuid is unique within a track.
   */
  private surveyIndexCache = new WeakMap<object, SurveyIndex>();

  private surveyIndex(surveyData: any): SurveyIndex {
    if (!surveyData || typeof surveyData !== 'object') {
      return { byUuid: new Map(), byAssetId: new Map(), identifiers: new Set(), identifiersByTrack: new Map() };
    }

    const cached = this.surveyIndexCache.get(surveyData);
    if (cached) return cached;

    const byUuid = new Map<string, SurveyRowMatch[]>();
    const byAssetId = new Map<string, SurveyRowMatch[]>();
    const identifiers = new Set<string>();
    const identifiersByTrack = new Map<string, Set<string>>();

    const addIdentifier = (value: any, trackIdentifiers: Set<string>) => {
      const raw = value?.toString()?.trim()?.toUpperCase();
      if (!raw) return;
      identifiers.add(raw);
      identifiers.add(this.normalizePointId(raw));
      trackIdentifiers.add(raw);
      trackIdentifiers.add(this.normalizePointId(raw));
    };

    Object.keys(surveyData).forEach(trackKey => {
      const rows = surveyData[trackKey];
      if (!Array.isArray(rows)) return;

      // Registered before the rows are walked, so a track the survey lists but
      // leaves empty still counts as known.
      const trackIdentifiers = new Set<string>();
      identifiersByTrack.set(this.trackKeyOf(trackKey), trackIdentifiers);

      rows.forEach((item: any) => {
        if (item?.uuid) {
          byUuid.set(item.uuid, [...(byUuid.get(item.uuid) ?? []), { trackKey, item }]);
        }
        if (item['ASSET ID'] && item['ABS LOCATION'] !== undefined) {
          byAssetId.set(item['ASSET ID'], [...(byAssetId.get(item['ASSET ID']) ?? []), { trackKey, item }]);
        }
        addIdentifier(item['ASSET ID'], trackIdentifiers);
        addIdentifier(item['POINT ID'], trackIdentifiers);
        addIdentifier(item['SIGNAL ID'], trackIdentifiers);
        addIdentifier(item['GATE ID'], trackIdentifiers);
      });
    });

    const index: SurveyIndex = { byUuid, byAssetId, identifiers, identifiersByTrack };
    this.surveyIndexCache.set(surveyData, index);
    return index;
  }

  /**
   * Resolve a uuid to its survey row, narrowed to a track when one is known.
   * strictTrack is for crossings, where the track is part of the key rather than
   * a hint — without it one track would be handed the other track's row.
   */
  private findSurveyRowByUuid(
    uuid: string | undefined,
    trackName: string | undefined,
    byUuid: Map<string, SurveyRowMatch[]>,
    strictTrack = false
  ): any | undefined {
    if (!uuid) return undefined;

    const matches = byUuid.get(uuid);
    if (!matches?.length) return undefined;

    if (trackName) {
      const onTrack = matches.find(
        m => m.trackKey?.trim()?.toLowerCase() === trackName?.trim()?.toLowerCase()
      );
      if (onTrack) return onTrack.item;
      if (strictTrack) return undefined;
    }

    return matches.length === 1 ? matches[0].item : undefined;
  }

  /**
   * The single location a uuid resolves to. Location dropdowns collapse to this
   * value, since a uuid names one row and leaves nothing to choose between.
   */
  getLocationForUuid(
    uuid: string | undefined,
    trackName: string | undefined,
    surveyData: any,
    strictTrack = false
  ): number | undefined {
    const item = this.findSurveyRowByUuid(uuid, trackName, this.surveyIndex(surveyData).byUuid, strictTrack);

    const location = item?.['ABS LOCATION'];
    return location === null ? undefined : location;
  }

  /** The lowercased form track names are compared and indexed by. */
  private trackKeyOf(trackName: any): string {
    return trackName?.toString()?.trim()?.toLowerCase() ?? '';
  }

  /** Track name with case, spaces, new lines and underscores ignored, so "Up_Main" and "UP MAIN" are equal. */
  private normalizeTrackName(trackName: any): string {
    return (trackName ?? '').toString().replace(/[\s_]+/g, '').toUpperCase();
  }

  /**
   * The Station Data track this name points to. Station Data's track names win over the
   * survey's track name. '' when no track or more than one track matches, so the
   * dropdown is left empty instead of showing a wrong track.
   */
  matchStationTrackName(trackName: any, stationTracks: string[]): string {
    const tracks = stationTracks ?? [];
    if (tracks.includes(trackName)) return trackName;

    const key = this.normalizeTrackName(trackName);
    if (!key) return '';

    const matches = tracks.filter(track => this.normalizeTrackName(track) === key);
    return matches.length === 1 ? matches[0] : '';
  }

  /**
   * Rename the survey's track keys in survey_data and minmax_assets to the
   * Station Data track names. A key is left as it is when it matches no track,
   * or when another survey key matches the same track.
   */
  useStationTrackNames(apiResponse: any, stationTracks: string[]): void {
    if (!apiResponse?.survey_data) return;

    const surveyTracks = Object.keys(apiResponse.survey_data);
    const stationTrackOf = new Map(surveyTracks.map(surveyTrack => [surveyTrack, this.matchStationTrackName(surveyTrack, stationTracks)]));
    const newNames = new Map<string, string>();

    stationTrackOf.forEach((stationTrack, surveyTrack) => {
      if (!stationTrack) return;
      const keysOnSameTrack = surveyTracks.filter(other => stationTrackOf.get(other) === stationTrack);
      if (keysOnSameTrack.length === 1) newNames.set(surveyTrack, stationTrack);
    });

    const renameKeys = (data: any) => {
      if (!data) return data;
      
      // turn the object into key-value pairs.
      const entries = Object.entries(data);
    
      // rename each key when a replacement exists.
      const renamedEntries = entries.map(([track, value]) => {
        const newTrackName = newNames.get(track) ?? track;
        return [newTrackName, value];
      });
      
      // turn the pairs back into an object
      return Object.fromEntries(renamedEntries);
    };
    apiResponse.survey_data = renameKeys(apiResponse.survey_data);
    apiResponse.minmax_assets = renameKeys(apiResponse.minmax_assets);
  }

  /**
   * True only when neither identifier appears where the survey would list it.
   * Clearing a location is destructive, so absence defaults to station-wide
   * rather than a single-track lookup a stale track_name could fail.
   */
  private isAbsentFromSurvey(
    uuid: string | undefined,
    assetId: any,
    surveyData: any,
    trackName?: string
  ): boolean {
    const { byUuid, identifiers, identifiersByTrack } = this.surveyIndex(surveyData);

    const trackKey = this.trackKeyOf(trackName);
    const onTrack = trackKey ? identifiersByTrack.get(trackKey) : undefined;
    if (uuid && byUuid.has(uuid)) {
      if (!onTrack) return false;
      if (byUuid.get(uuid)!.some(m => this.trackKeyOf(m.trackKey) === trackKey)) return false;
    }

    // Without an asset id absence is unprovable: ensureIds() fabricates an id for
    // every asset, so an unmatched uuid cannot tell a hand-added row from a deleted one.
    const raw = assetId?.toString()?.trim()?.toUpperCase();
    if (!raw) return false;

    const pool = onTrack ?? identifiers;
    return !pool.has(raw) &&
      !pool.has(this.normalizePointId(raw)) &&
      !pool.has(raw.replace('P-', 'P'));
  }

  /**
   * The survey uuid carried by a station-data entity. Crossings expose it as
   * `uuid`; elsewhere it arrives on `id`, which ensureIds() also fills with a
   * client-generated value — a fabricated one simply matches nothing.
   */
  surveyUuidOf(entity: any): string | undefined {
    return entity?.uuid ?? entity?.id;
  }

  /**
   * Resolve an ASSET ID, preferring a row on the asset's own track before
   * falling back across tracks — the same order the location dropdowns use, so
   * a re-upload and the step it feeds cannot disagree on a duplicated id.
   */
  private findSurveyRowByAssetId(
    assetId: string | undefined,
    trackName: string | undefined,
    index: SurveyIndex
  ): any | undefined {
    if (!assetId) return undefined;

    const matches = index.byAssetId.get(assetId);
    if (!matches?.length) return undefined;

    if (trackName) {
      const trackKey = this.trackKeyOf(trackName);
      const onTrack = matches.find(m => this.trackKeyOf(m.trackKey) === trackKey);
      if (onTrack) return onTrack.item;
      if (index.identifiersByTrack.has(trackKey)) return undefined;
    }

    return matches[0].item;
  }

  /** Resolve an asset to its survey row: uuid first, ASSET ID as fallback. */
  private matchSurveyRow(
    uuid: string | undefined,
    assetId: string | undefined,
    trackName: string | undefined,
    index: SurveyIndex
  ): any | undefined {
    return this.findSurveyRowByUuid(uuid, trackName, index.byUuid) ??
      this.findSurveyRowByAssetId(assetId, trackName, index);
  }

  /**
   * Write the resolved location onto an asset. A stale location is dropped only
   * when overwriting AND the asset is absent from the survey — a lookup that
   * merely failed (wrong track, unmatched id format) leaves it untouched.
   */
  private applyLocation(
    target: any,
    locationKey: string,
    matchedItem: any | undefined,
    surveyOverwrite: boolean,
    absentFromSurvey: boolean
  ): void {
    const newLocation = matchedItem?.['ABS LOCATION'];

    if (matchedItem && newLocation !== undefined) {
      const oldLocation = target[locationKey];
      if (surveyOverwrite || !oldLocation || oldLocation === '' || oldLocation === '-') {
        target[locationKey] = newLocation;
      }
      return;
    }

    if (surveyOverwrite && absentFromSurvey) this.clearLocation(target, locationKey);
  }

  /**
   * Locations removed by a re-upload, held in memory so the steps can offer them
   * back. Deliberately NOT written onto station data — that object is serialized
   * to the backend, which must not receive UI-only keys.
   */
  private clearedLocations = new Map<string, any>();

  /** Set by applyBorderTagSignalBindings when a re-upload moves an S border tag. */
  private signalBorderTagLocationChanged = false;

 // Stores each S border tag's original location to detect changes after survey re-upload.
  private originalBorderTagLocations = new WeakMap<any, number | null>();

  /** S border tags for which captureBorderTagSignalBindings found no matching signal at all. */
  private missingSignalBorderTags = new Set<any>();

  /** Identity of a location slot, tolerating raw station data and mapped form shapes. */
  private clearedLocationKey(entity: any, locationKey: string): string {
    const identity = this.surveyUuidOf(entity) ??
      `${entity?.asset_id ?? entity?.assetId ?? ''}|${entity?.track_name ?? entity?.trackName ?? ''}`;

    return `${identity}::${locationKey}`;
  }

  /**
   * The survey track carrying this asset id, or '' when absent or ambiguous.
   *
   * Every step infers the track this way when station data has none, so the
   * re-upload mirrors it — otherwise a row whose track name is empty or stale is
   * skipped here and only picks up its location when the user opens that step.
   * The options exist because each step matches slightly differently; they are
   * deliberately per-type rather than a shared approximation.
   */
  private inferTrackForAsset(
    assetId: any,
    surveyData: any,
    opts: {
      fields: string[];
      normalize?: boolean;
      strategy?: 'first' | 'unique';
      trapPoints?: 'prefer' | 'exclude';
    }
  ): string {
    const target = opts.normalize
      ? this.normalizePointId(assetId)
      : assetId?.toString()?.trim()?.toUpperCase();
    if (!target) return '';

    const matches: string[] = [];

    for (const trackKey of Object.keys(surveyData || {})) {
      const rows = surveyData[trackKey];
      if (!Array.isArray(rows)) continue;

      for (const row of rows) {
        const isTrapPoint = row?.['is_trap_point'] === true;
        if (opts.trapPoints === 'exclude' && isTrapPoint) continue;

        const raw = opts.fields.map(field => row[field]).find(Boolean);
        if (!raw) continue;

        const id = opts.normalize
          ? this.normalizePointId(raw)
          : raw.toString().trim().toUpperCase();
        if (id !== target) continue;

        if (opts.trapPoints === 'prefer' && isTrapPoint) return trackKey;
        if (opts.strategy !== 'unique') return trackKey;

        matches.push(trackKey);
      }
    }

    return matches.length === 1 ? matches[0] : '';
  }

  /** Turnout rule: normalized point id, never a trap point row, unambiguous only. */
  private inferTrackForPointAsset(assetId: string, surveyData: any): string {
    return this.inferTrackForAsset(assetId, surveyData, {
      fields: ['ASSET ID', 'POINT ID'], normalize: true, strategy: 'unique', trapPoints: 'exclude'
    });
  }

  /**
   * Drop a location, remembering the removed value so the step can still offer it.
   * Clearing must not strand the user with an empty dropdown and no way back to
   * the number they had.
   */
  clearLocation(target: any, locationKey: string = 'location'): void {
    const cleared = target?.[locationKey];
    if (cleared !== undefined && cleared !== null && cleared !== '' && cleared !== '-') {
      this.clearedLocations.set(this.clearedLocationKey(target, locationKey), cleared);
    }

    target[locationKey] = null;
  }

  /**
   * Location dropdown options with any cleared value added back as a choice, so
   * a location removed by a re-upload stays selectable. Callers must keep this
   * out of their auto-pick source: restoring is the user's decision.
   */
  withClearedLocation(options: any[], entity: any, locationKey: string = 'location'): any[] {
    const list = options ?? [];
    const previous = this.clearedLocations.get(this.clearedLocationKey(entity, locationKey));
    if (previous === undefined || previous === null || previous === '' || previous === '-') return list;

    return list.some(o => o?.toString() === previous?.toString()) ? list : [...list, previous];
  }

  /**
   * Update signal locations: uuid (or ASSET ID) → Signal[].assets[].location.
   */
  updateSignalLocationsFromSurvey(stationData: any, apiResponse: any, surveyOverwrite: boolean): any {
    if (!stationData?.Signal || !apiResponse?.survey_data) return stationData;

    const index = this.surveyIndex(apiResponse.survey_data);

    stationData.Signal.forEach((track: any) => {
      if (!Array.isArray(track.assets)) return;
      track.assets.forEach((asset: any) => {
        // Nothing to look the asset up by — its location is hand-entered, leave it.
        if (!asset.id && !asset.asset_id) return;

        // Mirrors the signals step, which infers the track for assets in the
        // unassigned group rather than leaving them without one.
        const trackName = asset.track_name || track.track_name ||
          this.inferTrackForAsset(asset.original_asset_id ?? asset.asset_id, apiResponse.survey_data, {
            fields: ['ASSET ID', 'SIGNAL ID']
          });
        const matchedItem = this.matchSurveyRow(this.surveyUuidOf(asset), asset.asset_id, trackName, index);
        const absent = this.isAbsentFromSurvey(this.surveyUuidOf(asset), asset.asset_id, apiResponse.survey_data, trackName);
        this.applyLocation(asset, 'location', matchedItem, surveyOverwrite, absent);
      });
    });

    return stationData;
  }

  /** Returns true when the asset is marked as an assumed BSLB signal. */
  private isAssumedBSLBSignal(asset: any): boolean {
    return asset?.assumed_bslb_signal === true || asset?.assumed_bslb_signal === 'True';
  }

  /** Returns true when the asset is the Calling Home signal. */
  private isCallingHomeSignal(asset: any): boolean {
    return asset?.is_home_signal === true || asset?.is_home_signal === 'True';
  }

 /** Converts a location to a finite number, otherwise returns NaN. */
  private toLocationNumber(value: any): number {
    if (value === null || value === undefined || value === '' || value === '-') return NaN;
    const num = Number(value);
    return Number.isFinite(num) ? num : NaN;
  }


  /**
 * Captures the original distance between each assumed BSLB and its Calling
 * Home signal before survey locations are updated.
 */
  private captureAssumedBSLBOffsets(stationData: any): { bslb: any; homeSignal: any; diff: number }[] {
    const captures: { bslb: any; homeSignal: any; diff: number }[] = [];
    if (!Array.isArray(stationData?.Signal)) return captures;
    stationData.Signal.forEach((track: any) => {
      if (!Array.isArray(track.assets)) return;
      const assumedBSLBs = track.assets.filter((a: any) => this.isAssumedBSLBSignal(a));
      if (!assumedBSLBs.length) return;
      const homeSignal = track.assets.find((a: any) => this.isCallingHomeSignal(a)) ?? null;
      const oldCallingHomeLocation = homeSignal ? this.toLocationNumber(homeSignal.location) : NaN;
      assumedBSLBs.forEach((bslb: any) => {
        const oldBSLBLocation = this.toLocationNumber(bslb.location);
        captures.push({ bslb, homeSignal, diff: oldBSLBLocation - oldCallingHomeLocation });
      });
    });
    return captures;
  }

 /**
 * Restores each assumed BSLB location by applying its previously captured
 * offset to the Calling Home signal's updated location.
 */
  private applyAssumedBSLBLocations(captures: { bslb: any; homeSignal: any; diff: number }[]): void {
    captures.forEach(({ bslb, homeSignal, diff }) => {
      const newCallingHomeLocation = homeSignal ? this.toLocationNumber(homeSignal.location) : NaN;
      if (!homeSignal) {
        bslb.location = null;
        return;
      }
      bslb.location = newCallingHomeLocation + diff;
    });
  }

  /**
   * Update crossing/turnout locations: uuid (or POINT ID) → CROSSING[].Track X Location.
   * A crossing has one uuid but two survey rows, so the track name picks the row.
   */
  updatePointLocationsFromSurvey(stationData: any, apiResponse: any, surveyOverwrite: boolean): any {
    if (!stationData?.CROSSING || !apiResponse?.survey_data) return stationData;

    const surveyData = apiResponse.survey_data;
    const { byUuid } = this.surveyIndex(surveyData);
    const findTrackKey = (trackName: string) =>
      Object.keys(surveyData).find(
        k => k?.trim()?.toLowerCase() === trackName?.trim()?.toLowerCase()
      );

    const findByAssetId = (trackName: string, assetId: string) => {
      if (!trackName || !assetId) return undefined;
      const trackKey = findTrackKey(trackName);
      if (!trackKey || !Array.isArray(surveyData[trackKey])) return undefined;

      const normalizedAssetId = this.normalizePointId(assetId);
      return surveyData[trackKey].find((item: any) => {
        const pointId = item['ASSET ID'] || item['POINT ID'];
        // Trap point rows are excluded here as they are in the turnout dropdown,
        // so a turnout never adopts a trap point's location.
        if (item?.['is_trap_point']) return false;
        return pointId === normalizedAssetId || pointId == assetId ||
          pointId === normalizedAssetId?.replace('-', '');
      });
    };

    const applyTrack = (crossing: any, trackName: string, assetId: string, locationKey: string, mayInferTrack: boolean) => {
      if (!crossing.id && !assetId) return;

      let matchingItem =
        this.findSurveyRowByUuid(crossing.id, trackName, byUuid, true) ??
        findByAssetId(trackName, assetId);

      // Stored track name missing or stale — fall back to the track the point id
      // itself identifies, which is what the turnout step does.
      if (!matchingItem && mayInferTrack) {
        const inferredTrack = this.inferTrackForPointAsset(assetId, surveyData);
        if (inferredTrack && inferredTrack !== trackName) {
          matchingItem =
            this.findSurveyRowByUuid(crossing.uuid, inferredTrack, byUuid, true) ??
            findByAssetId(inferredTrack, assetId);
        }
      }

      const absent = this.isAbsentFromSurvey(crossing.uuid, assetId, surveyData);
      this.applyLocation(crossing, locationKey, matchingItem, surveyOverwrite, absent);
    };

    stationData.CROSSING.forEach((crossing: any) => {
      // When both tracks name the same point id, inference cannot tell them apart
      // and would give both tracks one row; leave those to the step's own resolver.
      const mayInferTrack = crossing['SIP_Track_1_asset_id'] !== crossing['SIP_Track_2_asset_id'];

      applyTrack(crossing, crossing['Track 1'], crossing['SIP_Track_1_asset_id'], 'Track 1 Location', mayInferTrack);
      applyTrack(crossing, crossing['Track 2'], crossing['SIP_Track_2_asset_id'], 'Track 2 Location', mayInferTrack);
    });

    return stationData;
  }

  /**
   * Update trap point locations: POINT ID/ASSET ID → Trap Point[].location.
   */
  updateTrapPointLocationsFromSurvey(stationData: any, apiResponse: any, surveyOverwrite: boolean): any {
    if (!stationData?.['Trap Point'] || !apiResponse?.survey_data) return stationData;

    const surveyData = apiResponse.survey_data;
    const { byUuid } = this.surveyIndex(surveyData);

    const findByAssetId = (trackName: string, assetId: string) => {
      if (!assetId || !trackName) return undefined;

      const trackKey = Object.keys(surveyData).find(
        k => k?.trim()?.toLowerCase() === trackName?.trim()?.toLowerCase()
      );
      if (!trackKey || !Array.isArray(surveyData[trackKey])) return undefined;

      const normalizedAssetId = this.normalizePointId(assetId);
      return surveyData[trackKey].find((item: any) => {
        const surveyAssetId = item['ASSET ID'] || item['POINT ID'];
        return this.normalizePointId(surveyAssetId) === normalizedAssetId;
      });
    };

    stationData['Trap Point'].forEach((trapPoint: any) => {
      if (!trapPoint.id && !trapPoint.asset_id) return;

      // Mirrors the trap point step, which infers the track when the stored one
      // is missing or a '-' placeholder, preferring an actual trap point row.
      const storedTrack = trapPoint.track_name && trapPoint.track_name !== '-' ? trapPoint.track_name : '';
      const trackName = storedTrack || this.inferTrackForAsset(trapPoint.asset_id, surveyData, {
        fields: ['ASSET ID', 'POINT ID'], normalize: true, strategy: 'unique', trapPoints: 'prefer'
      });

      const matchingItem =
        this.findSurveyRowByUuid(this.surveyUuidOf(trapPoint), trackName, byUuid) ??
        findByAssetId(trackName, trapPoint.asset_id);

      const absent = this.isAbsentFromSurvey(this.surveyUuidOf(trapPoint), trapPoint.asset_id, surveyData);
      this.applyLocation(trapPoint, 'location', matchingItem, surveyOverwrite, absent);
    });

    return stationData;
  }

  /**
    * Update dead end locations in stationData using survey data from API response.
    */
  private updateDeadEndLocationsFromSurvey(stationData: any, apiResponse: any, surveyOverwriteKey: boolean): any {
    if (!stationData?.['DEAD END'] || !apiResponse?.survey_data) {
      return stationData;
    }

    const surveyData = apiResponse.survey_data;
    const surveyOverwrite = surveyOverwriteKey;
    const { byUuid } = this.surveyIndex(surveyData);
    let skippedcount = 0;

    stationData['DEAD END'].forEach((deadEnd: any) => {
      // A uuid identifies the row outright, so the duplicate-ASSET-ID guard
      // below does not apply to uuid-linked dead ends.
      const uuidMatch = this.findSurveyRowByUuid(this.surveyUuidOf(deadEnd), deadEnd.track_name, byUuid);
      if (uuidMatch) {
        this.applyLocation(deadEnd, 'location', uuidMatch, surveyOverwrite, false);
        return;
      }

      const assetId = deadEnd.asset_id;
      if (!assetId) return;

      // Mirrors the dead end step, which infers the track from the asset id when
      // station data has none.
      const trackName = deadEnd.track_name || this.inferTrackForAsset(assetId, surveyData, {
        fields: ['ASSET ID', 'SIGNAL ID', 'POINT ID']
      });
      if (!trackName) return;

      const trackKey = Object.keys(surveyData).find(
        k => k?.trim()?.toLowerCase() === trackName?.trim()?.toLowerCase()
      );

      const normalizedAssetId = assetId.trim().toUpperCase();
      if (!this.hasAmbiguousAssetIdOnTrack(stationData['DEAD END'], trackName, assetId)) {
        const matchingItem = Array.isArray(surveyData[trackKey as string])
          ? surveyData[trackKey as string].find((item: any) => {
            const pointId = item['POINT ID'];
            const signalId = item['ASSET ID'] || item['SIGNAL ID'];
            return pointId?.trim()?.toUpperCase() === normalizedAssetId ||
              pointId?.trim()?.toUpperCase() === normalizedAssetId.replace('P-', 'P') ||
              signalId?.trim()?.toUpperCase() === normalizedAssetId;
          })
          : undefined;
        const absent = this.isAbsentFromSurvey(this.surveyUuidOf(deadEnd), assetId, surveyData);
        this.applyLocation(deadEnd, 'location', matchingItem, surveyOverwrite, absent);
      } else {
        // Ambiguous by asset id alone. Respect the overwrite flag here too:
        // wiping a location the user kept would contradict "don't overwrite".
        if (surveyOverwrite) this.clearLocation(deadEnd);
        skippedcount += 1;
      }
    });
    if (skippedcount > 0) {

      this.toast.showWarn(`Skipped updating location for ${skippedcount} dead ends due to duplicate asset IDs in the same track, which can cause ambiguity in location assignment.`)
    }
    return stationData;
  }

  /**
   * True when several dead ends on the same track share an asset id, leaving the
   * survey rows ambiguous. Callers must offer the choice rather than guess — auto
   * picking would give every duplicate the same location.
   */
  hasAmbiguousAssetIdOnTrack(deadEnds: any[], trackName: string, assetId: string): boolean {
    const target = assetId?.toString()?.trim()?.toUpperCase();
    if (!target || !trackName) return false;

    const occurrences = (deadEnds ?? []).filter((item: any) =>
      item?.track_name === trackName &&
      item?.asset_id?.toString()?.trim()?.toUpperCase() === target
    );

    return occurrences.length > 1;
  }

  
  updateUtilLocationsFromSurvey(stationData: any, apiResponse: any, surveyOverwrite: boolean): any {
    if (!Array.isArray(stationData?.utils) || !apiResponse?.survey_data) return stationData;

    const surveyData = apiResponse.survey_data;
    const { byUuid } = this.surveyIndex(surveyData);
    const findTrackKey = (trackName: string) =>
      Object.keys(surveyData).find(
        k => k?.trim()?.toLowerCase() === trackName?.trim()?.toLowerCase()
      );

    const findInTrack = (trackName: string, assetId: string) => {
      const trackKey = findTrackKey(trackName);
      if (!trackKey || !Array.isArray(surveyData[trackKey])) return undefined;

      return surveyData[trackKey].find((item: any) => {
        const id = (item['ASSET ID'] || item['GATE ID'])?.toString()?.trim();
        return id === assetId;
      });
    };

    stationData.utils.forEach((util: any) => {
      const type = util?.type_of_asset;
      if (type !== 'Gate' && type !== 'roadBridge') return;

      const assetId = util.asset_id?.toString()?.trim();
      if (!util.id && !assetId) return;

      // A roadBridge spans several tracks; a Gate sits on exactly one. Fall back
      // to the track the asset id identifies when station data carries none.
      const inferredTrack = () => this.inferTrackForAsset(assetId, surveyData, {
        fields: ['ASSET ID', 'GATE ID']
      });
      const tracks: string[] = type === 'roadBridge'
        ? (Array.isArray(util.tracks) && util.tracks.length ? util.tracks : [inferredTrack()])
        : [util.track_name || inferredTrack()];

      let matchingItem: any;
      for (const trackName of tracks) {
        matchingItem = this.findSurveyRowByUuid(this.surveyUuidOf(util), trackName, byUuid);
        if (matchingItem) break;

        if (!trackName || !assetId) continue;
        matchingItem = findInTrack(trackName, assetId);
        if (matchingItem && matchingItem['ABS LOCATION'] !== undefined) break;
      }

      // Single-track utils still resolve when the track name is missing or stale.
      matchingItem ??= this.findSurveyRowByUuid(this.surveyUuidOf(util), undefined, byUuid);

      const absent = this.isAbsentFromSurvey(this.surveyUuidOf(util), assetId, surveyData);
      this.applyLocation(util, 'location', matchingItem, surveyOverwrite, absent);
    });

    return stationData;
  }

  // ==================== Border Tags ====================

  /**
   * Pair every S border tag with the signal assets sitting at its location,
   * resolved before the survey update moves those signals.
   *
   * The Adjustment Points step picks an S tag by Asset Id but persists only the
   * location that choice produced, so the old location is the sole link back to
   * the signal. N and X tags are hand-entered ABS values and are left alone.
   */
  private captureBorderTagSignalBindings(stationData: any): BorderTagSignalBinding[] {
    const trackEntries = stationData?.input_border_tags_data;
    if (!Array.isArray(trackEntries) || !Array.isArray(stationData?.Signal)) return [];

    const bindings: BorderTagSignalBinding[] = [];

    trackEntries.forEach((trackEntry: any) => {
      if (!Array.isArray(trackEntry?.manual_border_tags)) return;

      const assetsOnTrack = this.signalAssetsOnTrack(stationData.Signal, trackEntry.track_name);

      trackEntry.manual_border_tags.forEach((tag: any) => {
        if (tag?.type_of_tag !== SIGNAL_BORDER_TAG_TYPE) return;

        const location = roundedLocation(tag.location);
        if (location === null) {
          // No location to look a signal up by at all — nothing for this tag to
          // resolve to, same as never finding a matching asset.
          this.missingSignalBorderTags.add(tag);
          return;
        }

        const assets = assetsOnTrack.filter(asset => roundedLocation(asset?.location) === location);
        if (assets.length) {
          bindings.push({ tag, assets });
        } else {
          // No signal sits at this tag's recorded location on its track at all —
          // distinct from a bound tag whose signal later moved or was cleared.
          this.missingSignalBorderTags.add(tag);
        }
      });
    });

    return bindings;
  }

  /**
   * The track a signal asset belongs to, read off the survey rows.
   *
   * The whole asset id wins outright; otherwise a combination id ("S1 S2") only
   * resolves when every part the survey knows sits on the same track, since a
   * split verdict is no verdict at all.
   */
  findTrackForSignalAsset(asset: any, surveyData: any): string {
    if (!surveyData) return '';

    const assetId = asset?.original_asset_id ?? asset?.asset_id;
    const ids = assetId?.split(' ')?.map((id: string) => id.trim().toUpperCase())?.filter(Boolean);
    if (!ids?.length) return '';

    const trackNames: string[] = [];

    for (const [trackName, rows] of Object.entries(surveyData)) {
      for (const row of rows as any[]) {
        const surveyAssetId = row['ASSET ID'] || row['SIGNAL ID'];

        if (!surveyAssetId) continue;
        const normalizedSurveyId = surveyAssetId.trim()?.toUpperCase();
        if (assetId?.trim()?.toUpperCase() === normalizedSurveyId) {
          return trackName;
        }

        if (ids.includes(normalizedSurveyId)) {
          trackNames.push(trackName);
        }
      }
    }
    return trackNames.length == 1 ? trackNames[0] : '';
  }

  /**
   * The survey locations an asset id resolves to on a track, in the order the
   * Signals step offers them. Moved here from the Signals step so the same list
   * backs both its dropdown and the regrouping below.
   */
  getLocationDropdownList(assetId: string, trackName: string, survey_data: any, uuid?: string): number[] {
    // A uuid names exactly one survey row, so there is nothing to choose between.
    const uuidLocation = this.getLocationForUuid(uuid, trackName, survey_data);
    if (uuidLocation !== undefined) return [uuidLocation];

    let matchedAssets: any[] = [];
    const trackKey = Object.keys(survey_data)?.find(
      k => k?.trim()?.toLowerCase() === trackName?.trim()?.toLowerCase()
    );

    const normalizedAssetId = assetId?.trim()?.toUpperCase();

    // Step 1: try inside the same track
    if (trackKey && survey_data?.[trackKey]) {
      matchedAssets = survey_data[trackKey].filter(
        (item: any) => {
          const assetId = item['ASSET ID'] || item['SIGNAL ID'];
          return assetId?.trim()?.toString()?.toUpperCase() === normalizedAssetId
        }
      );
    }
    // Step 2: fallback → search across all tracks, but only while the asset's own
    if (matchedAssets.length === 0 && !trackKey) {
      Object.keys(survey_data || {}).forEach(trackKey => {
        const trackItems = survey_data?.[trackKey] || [];
        trackItems.forEach((item: any) => {
          const assetId = item['ASSET ID'] || item['SIGNAL ID']
          if (assetId?.trim()?.toString()?.toUpperCase() === normalizedAssetId) {
            matchedAssets.push(item);
          }
        });
      });
    }
    // Step 3: collect only valid ABS LOCATION values
    return matchedAssets.map(item => item["ABS LOCATION"]).filter((loc: any) => loc != null);
  }

  /**
   * True when signals are still sitting outside any track — the ones a track filter
   * would drop. The only case regrouping has anything to do.
   */
  hasUnassignedSignalAssets(stationData: any): boolean {
    if (!Array.isArray(stationData?.Signal)) return false;

    return stationData.Signal.some(
      (group: any) => !this.trackKeyOf(group?.track_name) && group?.assets?.length > 0
    );
  }

  /**
   * Signal assets listed under a track, keyed on the signal group's track_name —
   * the same key the Asset Id dropdown builds its options from, so a tag can only
   * ever re-bind to a signal it could have been picked from.
   */
  private signalAssetsOnTrack(signalGroups: any[], trackName: any): any[] {
    const target = trackName?.toString()?.trim()?.toLowerCase();
    if (!target) return [];

    return signalGroups
      .filter((group: any) => group?.track_name?.toString()?.trim()?.toLowerCase() === target)
      .flatMap((group: any) => Array.isArray(group?.assets) ? group.assets : []);
  }

  /**
   * Move each S border tag onto its signal's new location.
   *
   * A signal the survey dropped leaves its location null, which the tag adopts —
   * the Adjustment Points step then shows it as unfilled and blocks the step
   * until a fresh Asset Id is picked, rather than carrying a border nothing sits at.
   */
  private applyBorderTagSignalBindings(bindings: BorderTagSignalBinding[]): void {
    bindings.forEach(({ tag, assets }) => {
      if (!this.originalBorderTagLocations.has(tag)) {
        this.originalBorderTagLocations.set(tag, roundedLocation(tag.location));
      }
      const originalLocation = this.originalBorderTagLocations.get(tag) ?? null;

      const locations = new Set(assets.map(asset => roundedLocation(asset?.location)));

      // Several signals shared the old location and the survey split them apart:
      // nothing says which one the tag meant, and guessing would move a border.
      if (locations.size !== 1) return;

      const newLocation = locations.values().next().value;
      if (newLocation === null) {
        // The signal this tag was bound to was dropped by the survey — there is
        // no longer any signal for it to point at, so this is a "missing"
        // problem, not merely a relocation.
        this.missingSignalBorderTags.add(tag);
      } else if (originalLocation !== newLocation) {
        this.signalBorderTagLocationChanged = true;
      }
      tag.location = newLocation;
    });
  }

}
